/**
 * /sources — linked WhatsApp groups, ingestion toggles, per-group import status.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { Check, FileText, Loader2, MessageSquare, Plus, Users } from 'lucide-react';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { EmptyState } from '@/components/patterns/empty-state';
import { IconTile } from '@/components/patterns/icon-tile';
import { PageHeader } from '@/components/patterns/page-header';
import { StatusPill } from '@/components/patterns/status-pill';
import { GranolaConnectedAccounts } from '@/components/sources/granola-connected-accounts';
import { SignalConnectedAccounts } from '@/components/sources/signal-connected-accounts';
import { SignalImports } from '@/components/sources/signal-imports';
import { SlackConnectedAccounts } from '@/components/sources/slack-connected-accounts';
import { WahaConnectedAccounts } from '@/components/sources/waha-connected-accounts';
import { buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { connectorApi, type Group, type ImportBatch, importsApi, wahaApi } from '@/lib/api';
import { qk } from '@/lib/query-client';
import { cn } from '@/lib/utils';

const IMPORT_POLL_MS = 3_000;

function freshness(staleAt: string | null): 'Active' | 'Stale' {
  if (!staleAt) return 'Active';
  return new Date(staleAt) < new Date() ? 'Stale' : 'Active';
}

/** Exported for tests — the page itself renders it from the group list. */
export function GroupCard({
  group,
  activeBatch,
  doneBatch,
}: {
  group: Group;
  activeBatch: ImportBatch | undefined;
  doneBatch: ImportBatch | undefined;
}) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => connectorApi.patchCollection(group.id, enabled),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.groups }),
    onError: () => toast.error('Failed to update ingestion'),
  });

  const rejoin = useMutation({
    mutationFn: (jid: string) => wahaApi.rejoin(jid),
    onSuccess: () => {
      toast.success('Brain rejoined the group and is indexing it again.');
      void qc.invalidateQueries({ queryKey: qk.groups });
      void qc.invalidateQueries({ queryKey: qk.wahaGroups });
    },
    onError: (err) => {
      const status = (err as { status?: number }).status;
      if (status === 409)
        toast.error('That invite link no longer works — reconnect from Add source.');
      else toast.error("Couldn't rejoin the group. Please try again.");
    },
  });

  // Brain is not in this group. The ingestion flag is meaningless here — it is a
  // preference about a chat we no longer receive — so it must not be what the
  // card reports, and toggling it would promise indexing that cannot happen.
  const left = group.membership === 'left';

  const metaText = left
    ? 'Brain is no longer in this group'
    : group.lastActivityAt
      ? `Last activity ${new Date(group.lastActivityAt).toLocaleDateString()} · ${freshness(group.staleAt)}`
      : `No activity yet · ${freshness(group.staleAt)}`;

  return (
    <div className="rounded-xl border border-border p-6">
      <div className="flex items-center gap-4">
        <IconTile tone="neutral">
          <Users className="h-5 w-5" />
        </IconTile>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold">{group.title}</p>
          <p className="truncate font-mono text-[13px] text-faint">{metaText}</p>
        </div>
        {left ? (
          <StatusPill tone="warning">Not in group</StatusPill>
        ) : (
          <StatusPill tone={group.ingestionEnabled ? 'active' : 'paused'}>
            {group.ingestionEnabled ? 'Indexing' : 'Paused'}
          </StatusPill>
        )}
        <Link
          to="/sources/collection/$collectionId"
          params={{ collectionId: group.id }}
          className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'shrink-0')}
        >
          View
        </Link>
        {left ? (
          group.canRejoin && group.groupJid ? (
            <button
              type="button"
              disabled={rejoin.isPending}
              onClick={() => group.groupJid && rejoin.mutate(group.groupJid)}
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'shrink-0')}
            >
              {rejoin.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Rejoin'}
            </button>
          ) : null
        ) : (
          <button
            type="button"
            role="switch"
            aria-checked={group.ingestionEnabled}
            aria-label={group.ingestionEnabled ? 'Disable ingestion' : 'Enable ingestion'}
            disabled={toggle.isPending}
            onClick={() => toggle.mutate(!group.ingestionEnabled)}
            className={cn(
              'relative h-6 w-[42px] shrink-0 rounded-full transition-colors disabled:opacity-50',
              group.ingestionEnabled ? 'bg-primary' : 'bg-[#e2e2e5]',
            )}
          >
            <span
              className={cn(
                'absolute left-[3px] top-1/2 h-[18px] w-[18px] -translate-y-1/2 rounded-full bg-white transition-transform',
                group.ingestionEnabled && 'translate-x-[18px]',
              )}
            />
          </button>
        )}
      </div>

      {/* History import is connector-specific: WhatsApp backfills a single group
          from a .txt/.zip export (per-collection importer); Signal's importer is
          account-wide (one backup carries every chat). Other connectors (Slack)
          have no export-import path, so no bar. */}
      {(group.connector === 'whatsapp' || group.connector === 'signal') && (
        <div className="mt-4 flex items-center gap-3 border-t border-border-subtle pt-4">
          {activeBatch ? (
            <>
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">Importing chat history…</p>
                <p className="truncate text-[13px] text-faint">
                  {activeBatch.status === 'indexing' ? 'Indexing messages' : 'Parsing export'}
                  {activeBatch.counts
                    ? ` · ${activeBatch.counts.imported.toLocaleString()} imported so far`
                    : ''}
                </p>
              </div>
              <StatusPill tone="active">Importing</StatusPill>
            </>
          ) : doneBatch ? (
            <>
              <Check className="h-4 w-4 shrink-0 text-success" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">Chat history imported</p>
                <p className="truncate text-[13px] text-faint">
                  {doneBatch.filename} · {(doneBatch.counts?.imported ?? 0).toLocaleString()}{' '}
                  messages · {new Date(doneBatch.createdAt).toLocaleDateString()}
                </p>
              </div>
            </>
          ) : group.connector === 'signal' ? (
            <>
              <FileText className="h-4 w-4 shrink-0 text-faint" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">No history imported</p>
                <p className="text-[13px] text-faint">
                  Import a Signal export to backfill messages sent before Brain joined.
                </p>
              </div>
              <Link
                to="/sources/signal/import"
                className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'shrink-0')}
              >
                Import history
              </Link>
            </>
          ) : (
            <>
              <FileText className="h-4 w-4 shrink-0 text-faint" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">No chat history imported</p>
                <p className="text-[13px] text-faint">
                  Import a WhatsApp export once to backfill messages sent before Brain joined.
                </p>
              </div>
              <Link
                to="/sources/$collectionId/import"
                params={{ collectionId: group.id }}
                className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'shrink-0')}
              >
                Import history
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function SourcesPage() {
  const qc = useQueryClient();

  // The Slack OAuth callback redirects here as /sources?slack=connected. Announce
  // it once and refresh the Slack account list so the new card appears. The route
  // has no validateSearch, so read the param loosely.
  const search = useSearch({ strict: false }) as { slack?: string };
  useEffect(() => {
    if (search.slack === 'connected') {
      toast.success('Slack connected — your conversations are being indexed.');
      void qc.invalidateQueries({ queryKey: qk.slackAccounts });
    }
  }, [search.slack, qc]);

  const { data: groupsData, isLoading: groupsLoading } = useQuery({
    queryKey: qk.groups,
    queryFn: () => connectorApi.listGroups(),
  });

  const { data: importsData } = useQuery({
    queryKey: qk.imports,
    queryFn: () => importsApi.list(),
    refetchInterval: (query) => {
      const batches = (query.state.data as { batches: ImportBatch[] } | undefined)?.batches ?? [];
      const hasActive = batches.some((b) => b.status === 'parsing' || b.status === 'indexing');
      return hasActive ? IMPORT_POLL_MS : false;
    },
  });

  const groups = groupsData ?? [];
  const batches = importsData?.batches ?? [];

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title="Sources"
          subtitle="Groups managed by your Brain number. Toggle ingestion to pause or resume indexing, and import each group's chat history once."
        />
        <Link
          to="/sources/add"
          className={cn(buttonVariants({ variant: 'default', size: 'sm' }), 'shrink-0')}
        >
          <Plus className="h-4 w-4" />
          Add source
        </Link>
      </div>

      <WahaConnectedAccounts />
      <SignalConnectedAccounts />
      <SlackConnectedAccounts />
      <GranolaConnectedAccounts />
      <SignalImports />

      {groupsLoading ? (
        <div className="mt-8 flex flex-col gap-4">
          <Skeleton className="h-[124px] w-full rounded-xl" />
          <Skeleton className="h-[124px] w-full rounded-xl" />
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          className="mt-8"
          icon={MessageSquare}
          title="No groups yet — complete the onboarding wizard to create one."
        />
      ) : (
        <div className="mt-8 flex flex-col gap-4">
          {groups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              activeBatch={batches.find(
                (b) =>
                  b.collectionId === group.id &&
                  (b.status === 'parsing' || b.status === 'indexing'),
              )}
              doneBatch={batches.find((b) => b.collectionId === group.id && b.status === 'done')}
            />
          ))}
        </div>
      )}
    </div>
  );
}
